/**
 * Update date: 07-06-2023
 * Screen 2081.5
 */
import Collapse from 'app/components/Collapse';
import DateOptions from 'app/components/DateOptions';
import PopupSelectCommonCode from 'app/components/PopupCommon/PopupSelectCommonCode';
import PopupSelectMall from 'app/components/PopupCommon/PopupSelectMall';
import PopupSelectStore from 'app/components/PopupCommon/PopupSelectStore';
import PopupSelectUser from 'app/components/PopupCommon/PopupSelectUser';
import Form from 'devextreme-react/form';
import useFormatTime from 'hooks/useFormatTime';
import { cloneDeep, uniqBy } from 'lodash';
import moment from 'moment';
import { useRef, useState } from 'react';
import { useDepositCheckByOrder } from '..';

const FormSearch = () => {
  const rootContext: any = useDepositCheckByOrder();
  const { t, formSearchData, setFormSearchData, refetchData } =
    rootContext || {};
  const [showPopupByType, setShowPopupByType] = useState('');
  const formRef: any = useRef(null);
  const collapseRef: any = useRef(null);
  const { DateFormat } = useFormatTime();

  // handle search
  const onSubmit = async () => {
    collapseRef?.current?.onCollappse();
    const isValid = formRef?.current?.instance?.validate()?.isValid;
    if (isValid) {
      const searchParams = {
        fromDate: formSearchData?.fromDate
          ? moment(formSearchData?.fromDate).format('YYYY-MM-DD')
          : null,
        endDate: formSearchData?.toDate
          ? moment(formSearchData?.toDate).format('YYYY-MM-DD')
          : null,
        mallCds: formSearchData.dataList.mall?.map((o: any) => o?.mallCode),
        productTypes: formSearchData.dataList.productType?.map(o => o?.code),
        storeIds: formSearchData.dataList.store?.map((o: any) => o.storeNo),
        sendUserIds: formSearchData.dataList.sendUser?.map(
          (o: any) => o.userId,
        ),
        deposit: formSearchData?.deposit,
        invoice: formSearchData?.invoice,
      };

      refetchData({
        data: searchParams,
      });
    } else {
      collapseRef?.current?.onCollappse();
    }
  };

  // remote data input popup
  const removeStore = (storedType: string, index: number) => {
    const dataList = cloneDeep(formSearchData?.dataList || {});
    if (index === -1) {
      dataList[storedType] = [];
    } else {
      dataList[storedType]?.splice(index, 1);
    }
    setFormSearchData({
      ...formSearchData,
      dataList: dataList,
    });
  };
  // update data input popup
  const updateStore = (storedType: string, data, key: any = false) => {
    const dataList = cloneDeep(formSearchData?.dataList || {});
    let filterData: any = [data];
    if (key) {
      filterData = uniqBy([...(dataList?.[storedType] || []), ...data], key);
    }
    dataList[storedType] = filterData;
    const newData = {
      ...formSearchData,
      dataList: dataList,
      [storedType]: '',
    };
    setFormSearchData(newData);
    setShowPopupByType('');
  };
  // generate form input
  const generateDxTextBox = (
    fieldLabel: string,
    fieldName: string,
    fieldBtnText: string,
    readOnly = false,
    numberShowMore = 2,
    colSpan = 0,
  ): any => {
    const dataList = formSearchData?.dataList?.[fieldName] || [];
    const buttons = dataList.slice(0, numberShowMore).map((data, index) => {
      return {
        name: `${fieldName}${index}`,
        location: 'before',
        options: {
          icon: 'close',
          text: data?.[fieldBtnText],
          onClick: () => removeStore(fieldName, index),
        },
      };
    });
    if (dataList.length > numberShowMore) {
      buttons.push({
        name: `${fieldName}More`,
        location: 'before',
        options: {
          text: `+ ${dataList.length - numberShowMore} ${t('more')}`,
        },
      });
    }
    if (dataList.length > 0) {
      buttons.push({
        name: 'clearAll',
        location: 'after',
        options: {
          stylingMode: 'text',
          icon: 'close',
          onClick: () => removeStore(fieldName, -1),
        },
      });
    }
    buttons.push({
      name: 'search',
      location: 'after',
      options: {
        stylingMode: 'text',
        icon: 'search',
        onClick: () => setShowPopupByType(fieldName),
      },
    });
    return {
      label: { text: t(fieldLabel) },
      dataField: fieldName,
      editorType: 'dxTextBox',
      colSpan: colSpan,
      editorOptions: {
        readOnly: readOnly || false,
        onEnterKey: () => setShowPopupByType(fieldName),
        showClearButton: true,
        buttons: buttons,
      },
    };
  };
  // render popup select
  const getPopupByType = (popupType: string) => {
    switch (popupType) {
      case 'mall':
        return (
          <PopupSelectMall
            text={formSearchData?.[popupType]}
            mode="single"
            visible={showPopupByType === popupType}
            onHiding={() => setShowPopupByType('')}
            onSubmit={o => updateStore('mall', o)}
          />
        );
      case 'sendUser':
        return (
          <PopupSelectUser
            text={formSearchData?.[popupType]}
            mode="multiple"
            visible={showPopupByType === popupType}
            onHiding={() => setShowPopupByType('')}
            onSubmit={o => updateStore('sendUser', o, 'userId')}
            dataBody={{ roleType: 'Delivery' }}
          />
        );
      case 'store':
        return (
          <PopupSelectStore
            text={formSearchData?.[popupType]}
            mode="multiple"
            visible={showPopupByType === popupType}
            onHiding={() => setShowPopupByType('')}
            onSubmit={o => updateStore('store', o, 'storeNo')}
          />
        );
      case 'productType':
        return (
          <PopupSelectCommonCode
            text={formSearchData?.[popupType]}
            codeType={'PT'}
            mode="multiple"
            visible={showPopupByType === popupType}
            onHiding={() => setShowPopupByType('')}
            onSubmit={o => updateStore(popupType, o, 'code')}
          />
        );
      default:
        return null;
    }
  };
  // on field data change
  const onFieldDataChanged = e => {
    switch (e?.dataField) {
      case 'fromDate':
        if (
          formSearchData.toDate &&
          e?.value &&
          e?.value > formSearchData.toDate
        ) {
          formRef?.current?.instance.updateData('toDate', null);
        }
        break;
      case 'toDate':
        if (
          formSearchData.fromDate &&
          e?.value &&
          e?.value < formSearchData.fromDate
        )
          formRef?.current?.instance.updateData('fromDate', null);
        break;
      default:
        break;
    }
  };

  // Onchange date when choose date option
  const onChangeDate = (fromDateValue, toDateValue) => {
    formRef?.current?.instance?.updateData('fromDate', fromDateValue);
    formRef?.current?.instance?.updateData('toDate', toDateValue);
  };

  /**
   * render search
   *
   * @param {*} { name }
   * @return {*}
   */
  const Search = ({ name }) => {
    return (
      <>
        <DateOptions onChangeValue={onChangeDate} />
        <Form
          ref={formRef}
          formData={formSearchData}
          showColonAfterLabel={false}
          items={[
            {
              label: { text: t('From Date') },
              dataField: 'fromDate',
              editorType: 'dxDateBox',
              editorOptions: {
                displayFormat: DateFormat,
              },
            },
            {
              label: { text: t('End Date') },
              dataField: 'toDate',
              editorType: 'dxDateBox',
              editorOptions: {
                displayFormat: DateFormat,
              },
            },
            generateDxTextBox('Mall ID', 'mall', 'mallCode', false, 1, 1),
            {
              label: { text: t('Deposit') },
              dataField: 'deposit',
              editorType: 'dxRadioGroup',
              editorOptions: {
                displayExpr: 'label',
                valueExpr: 'value',
                layout: 'horizontal',
                items: [
                  { label: t('All'), value: '' },
                  { label: t('Undeposit'), value: '1' },
                  { label: t('Deposit'), value: '2' },
                ],
              },
            },
            {
              label: { text: t('Invoice') },
              dataField: 'invoice',
            },
            generateDxTextBox('Store', 'store', 'storeNm'),
          ]}
          labelLocation="top"
          colCount={3}
          onFieldDataChanged={onFieldDataChanged}
        />
      </>
    );
  };

  /**
   * render advanced search
   *
   * @param {*} { name }
   * @return {*}
   */
  const AdvancedSearch = ({ name }) => {
    return (
      <>
        <Form
          formData={formSearchData}
          showColonAfterLabel={false}
          items={[
            generateDxTextBox(
              'Product Type',
              'productType',
              'name',
              formSearchData?.isReadOnlyProductTp,
            ),
            generateDxTextBox('Delivery User', 'sendUser', 'name'),
          ]}
          labelLocation="top"
          colCount={2}
          onFieldDataChanged={onFieldDataChanged}
        />
      </>
    );
  };

  return (
    <>
      <Collapse onSubmit={onSubmit} ref={collapseRef}>
        <Search name={t('Search')} />
        <AdvancedSearch name={t('Advanced Search')} />
      </Collapse>
      {getPopupByType(showPopupByType)}
    </>
  );
};

export default FormSearch;
